import { memo } from 'react'
import { AppIcon } from '@/config'
import { UserItem } from './UserItem'
import { APIClient } from '@/services'
import { PropsOf } from '@codeleap/types'
import { ChatUser } from '@packages/chat'
import { AugmentedRenderItemInfo } from '@codeleap/web'
import { View, Text, List, CustomPlaceholder, Scroll, Loading } from '@/components'

type User = ChatUser & {
  icon?: AppIcon
}

export type UserListProps = Omit<PropsOf<typeof List>, 'renderItem'> & {
  data: User[]
  onPress?: (user: User) => void
  onSelect?: (user: User) => void
  selectable?: boolean
  usersSelected?: User['id'][]
  roleShown?: boolean
  letterSeparatorShown?: boolean
  noInteractableUsers?: string[]
}

export const UserList = ({ noInteractableUsers, selectable, usersSelected, ...props }: UserListProps) => {
  const { profile } = APIClient.Session.useSession()

  const renderItem = ({ item, index }: AugmentedRenderItemInfo<User>) => {
    const firstLetter = item.name?.charAt(0).toUpperCase() || '?'
    const prevItem = props?.data?.[index - 1]
    const shouldRender = !prevItem || prevItem?.name?.toUpperCase()?.startsWith(firstLetter) === false
    const isSelected = usersSelected?.some(user => user === item.id)
    const blockInteraction = noInteractableUsers?.some(user => user === item.id)
    const isLast = index === props.data.length - 1

    return (
      <MemoizedUserItem
        key={item.id}
        user={item}
        currentProfile={profile}
        isLastItem={isLast}
        blockInteraction={blockInteraction}
        isSelected={isSelected}
        HeaderComponent={shouldRender && props.letterSeparatorShown ?
          <Text
            text={firstLetter}
            style={['c1', 'semiBold', 'mt:1', 'px:3', 'py:1', {
              breakpoints: {
                mobile: ['px:2'],
              },
            }]}
          /> : null}
        onPress={() => props?.onPress?.(item)}
        onSelect={() => props?.onSelect?.(item)}
        selectable={selectable}
        roleShown={props?.roleShown}
      />
    )
  }

  return (
    <Scroll
      {...props}
      ListEmptyComponent={() => props?.isLoading ? <Loader /> : (
        <View debugName='UserList:emptyState' style={['mt:3']}>
          <CustomPlaceholder
            iconSize={46}
            iconName='users-01'
            title='No users found'
            description='There are no users here.'
          />
        </View>
      )}
      renderItem={renderItem as any}
    />
  )
}

function Loader() {
  return (
    <View style={['flex', 'center', 'paddingVertical:2']}>
      <Loading size={32} />
    </View>
  )
}

const MemoizedUserItem = memo(UserItem, (prevProps, nextProps) => {
  return prevProps.isSelected === nextProps.isSelected &&
    prevProps.isLastItem === nextProps.isLastItem &&
    prevProps.blockInteraction === nextProps.blockInteraction &&
    prevProps.roleShown === nextProps.roleShown &&
    prevProps.user.role === nextProps.user.role &&
    prevProps.currentProfile?.id === nextProps.currentProfile?.id &&
    prevProps.HeaderComponent === nextProps.HeaderComponent
})

